import React from "react";
import GenericForm, { FormFieldConfig } from "../../../../components/genericform/GenericForm";
import { useCustomerForm } from "../hooks/useCustomerForm";
import { sanitizePincode } from "../utils/validation";
import { useUser } from "src/business-types/grocery/components/UserContext";

interface CustomerFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}


interface SectionFieldConfig extends Omit<FormFieldConfig, "id" | "onChange" | "value"> {
  type: "section";
  id: string;
  label: string;
}

interface ExtendedFormFieldConfigType extends FormFieldConfig {
  gridColumn?: string;
}

type ExtendedFormFieldConfig = ExtendedFormFieldConfigType | SectionFieldConfig;

const CustomerForm: React.FC<CustomerFormProps> = ({ open, onClose, onSubmit }) => {
  const { formData, errors, updateField, handlePhoneChange, handleAlternativePhoneChange, validateForm, resetForm, isFormValid, handleInputChange } =
    useCustomerForm();
  const { user } = useUser()

  const handleSubmit = () => {
    if (!validateForm()) return;

    const phonesPayload = [
    ...(formData.phone ? [formData.phone] : []),
    ...(formData.alternativePhone ? [formData.alternativePhone] : []),
  ];

    const submitData = {
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      phones: phonesPayload,
      emails: formData.email ? [formData.email] : [],
      openingOutstandingBalance: Number(formData.osb) ?? 0.0,
      addresses: {
        addressLine1: formData.address,
        addressLine2: formData.address2,
        city: formData.address3,
        postalCode: formData.address4,
      },
    };

    // Call the onSubmit prop first
    onSubmit(submitData);
    
    // Then reset the form
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const fields: ExtendedFormFieldConfig[] = [
    // --- Personal Info ---
    { id: "firstName", label: "First Name", required: true, value: formData.firstName, onChange: (val) => updateField("firstName", val) },
    { id: "middleName", label: "Middle Name", value: formData.middleName, onChange: (val) => updateField("middleName", val) },
    { id: "lastName", label: "Last Name", value: formData.lastName, onChange: (val) => updateField("lastName", val) },
    { id: "phone", label: "Phone Number", required: true, value: formData.phone, onChange: (val) => handlePhoneChange(val, formData.alternativePhone), error: errors.duplicatePhone, helperText: errors.duplicatePhone ? "Alternate phone cannot be same as primary" : "" },
    { id: "altPhone", label: "Alternative Phone Number", value: formData.alternativePhone, onChange: (val) => handleAlternativePhoneChange(val, formData.phone), error: errors.duplicatePhone },
    { id: "email", label: "Email", value: formData.email, onChange: (val) => updateField("email", val), error: errors.email, helperText: errors.email ? "Invalid email format" : "" },
    ...(user?.businessType !== "Restaurant"? [
    { id: "osb", label: "Prev. out. Balance", value: formData.osb, onChange: (val: string) => handleInputChange("osb", val.replace(/\D/g, "").slice(0, 6)), },
      ]: []),

    // --- Address Section ---
    { id: "address-section", type: "section", label: "Address (Optional)" },
    { id: "street", label: "Street / Village", value: formData.address, onChange: (val) => updateField("address", val),}, // full width under label
    { id: "district", label: "District / State", value: formData.address2, onChange: (val) => updateField("address2", val),},
    { id: "city", label: "City", value: formData.address3, onChange: (val) => updateField("address3", val),},
    { id: "pincode", label: "Pincode / Zipcode", value: formData.address4, onChange: (val) => updateField("address4", sanitizePincode(val)),},
  ];

  return (
    <GenericForm
      open={open}
      title='Enter customer details'
      fields={fields}
      onClose={handleClose}
      onSubmit={handleSubmit}
      isFormValid={isFormValid}
      confirmLabel="Confirm"
      width="max-w-xl"
    />
  );
};

export default CustomerForm;
